import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { ToastService } from './toast.service';
import { LoggerService } from './logger.service';

declare var Razorpay: any;

export interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  handler: (response: RazorpayResponse) => void;
  prefill: {
    name: string;
    email: string;
    contact: string;
  };
  notes: {
    address: string;
  };
  theme: {
    color: string;
  };
  modal?: {
    ondismiss?: () => void;
  };
}

export interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

export interface PaymentRequest {
  amount: number;
  currency: string;
  receipt: string;
  notes: {
    customer_name: string;
    customer_email: string;
    customer_phone: string;
    delivery_address: string;
  };
}

export interface PaymentResponse {
  success: boolean;
  order_id?: string;
  payment_id?: string;
  signature?: string;
  error?: string;
  error_code?: string;
  status?: string;
}

export interface OrderResponse {
  success: boolean;
  order_id?: string;
  key_id?: string;
  error?: string;
}

export interface PaymentVerificationRequest {
  order_id: string;
  payment_id: string;
  signature: string;
}

export interface PaymentVerificationResponse {
  success: boolean;
  verified: boolean;
  error?: string;
  message?: string;
}

export interface PaymentDetailsResponse {
  success: boolean;
  payment?: {
    id: string;
    amount: number;
    currency: string;
    status: string;
    method: string;
    created_at: number;
    captured: boolean;
    description?: string;
    notes?: any;
  };
  error?: string;
}

export interface OrderDetailsResponse {
  success: boolean;
  order?: {
    id: string;
    amount: number;
    currency: string;
    status: string;
    created_at: number;
    receipt: string;
    notes?: any;
    payments: any[];
  };
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class RazorpayService {
  private readonly RAZORPAY_KEY = environment.razorpay?.key || 'rzp_test_YOUR_TEST_KEY';
  private readonly API_BASE_URL = 'https://api.razorpay.com/v1';
  private lastReceivedKeyId?: string;
  
  // Firebase Functions URLs - these will be used for production
  private readonly FIREBASE_FUNCTIONS_BASE = `https://${environment.firebaseFunctions.region}-${environment.firebaseFunctions.projectId}.cloudfunctions.net`;

  constructor(private http: HttpClient, private toastService: ToastService, private loggerService: LoggerService) {
    this.initializeRazorpay();
  }

  /**
   * Initialize Razorpay and check availability
   */
  private initializeRazorpay(): void {
    if (typeof window !== 'undefined') {
      this.checkRazorpayAvailability();
    }
  }

  /**
   * Check if Razorpay script is available
   */
  private checkRazorpayAvailability(): void {
    if (typeof window !== 'undefined') {
      if ((window as any)['Razorpay']) {
        this.toastService.success('Payment gateway ready!', { duration: 2000 });
      } else {
        this.waitForRazorpayScript();
      }
    }
  }

  /**
   * Wait for Razorpay script to load
   */
  private waitForRazorpayScript(): void {
    const maxAttempts = 50; // Maximum 5 seconds (50 * 100ms)
    let attempts = 0;
    
    const checkInterval = setInterval(() => {
      attempts++;
      
      if ((window as any)['Razorpay']) {
        clearInterval(checkInterval);
        this.toastService.success('Payment gateway ready!', { duration: 2000 });
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        this.toastService.error('Payment gateway failed to load. Please refresh the page.');
      }
    }, 100);
  }

  /**
   * Check if Razorpay is ready for payments
   */
  isRazorpayReady(): boolean {
    return typeof window !== 'undefined' && !!(window as any)['Razorpay'];
  }

  /**
   * Wait for Razorpay to be ready (returns a promise)
   */
  waitForRazorpayReady(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.isRazorpayReady()) {
        resolve(true);
        return;
      }

      const maxWaitTime = 10000; // 10 seconds
      const checkInterval = 100; // Check every 100ms
      let elapsed = 0;

      const interval = setInterval(() => {
        elapsed += checkInterval;
        
        if (this.isRazorpayReady()) {
          clearInterval(interval);
          resolve(true);
        } else if (elapsed >= maxWaitTime) {
          clearInterval(interval);
          reject(new Error('Razorpay failed to load within timeout'));
        }
      }, checkInterval);
    });
  }

  /**
   * Check if running in local development
   */
  private isLocalDevelopment(): boolean {
    return window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.includes('localhost');
  }

  /**
   * Get appropriate Razorpay key for environment
   */
  private getRazorpayKey(): string {
    // Prefer key_id received from backend (most secure), fallback to env public key
    const key = this.lastReceivedKeyId || this.RAZORPAY_KEY;
    return key;
  }

  /**
   * Create Razorpay order via Firebase Functions
   */
  createOrder(paymentRequest: PaymentRequest): Observable<OrderResponse> {
    console.log('🔑 Creating order with Razorpay key:', this.RAZORPAY_KEY);
    
    if (!this.isRazorpayReady()) {
      this.toastService.error('Payment gateway is not ready. Please wait a moment and try again.');
      return throwError(() => new Error('Razorpay not ready'));
    }

    // Validate payment request
    if (!this.validatePaymentRequest(paymentRequest)) {
      return throwError(() => new Error('Invalid payment request'));
    }

    // Use Firebase Functions for order creation
    return this.createOrderViaFirebaseFunction(paymentRequest);
  }

  /**
   * Validate payment request
   */
  private validatePaymentRequest(paymentRequest: PaymentRequest): boolean {
    if (!paymentRequest.amount || paymentRequest.amount <= 0) {
      this.toastService.error('Invalid payment amount');
      return false;
    }

    if (!paymentRequest.currency) {
      this.toastService.error('Currency is required');
      return false;
    }

    if (!paymentRequest.receipt) {
      this.toastService.error('Receipt ID is required');
      return false;
    }

    return true;
  }

  /**
   * Create order via Firebase Function
   */
  private createOrderViaFirebaseFunction(paymentRequest: PaymentRequest): Observable<OrderResponse> {
    // Use the Firebase Function endpoint
    const endpoint = `${this.FIREBASE_FUNCTIONS_BASE}/createOrder`;
    console.log('📡 Calling Firebase Function:', endpoint);

    return this.http.post<OrderResponse>(endpoint, paymentRequest).pipe(
      map(response => {
        if (response?.success && response.order_id) {
          // Capture backend-provided key_id for checkout initialization
          if (response.key_id) {
            this.lastReceivedKeyId = response.key_id;
          }
          console.log('✅ Order created:', response.order_id);
          return response;
        }
        console.error('❌ Order creation failed:', response);
        throw new Error(response?.error || 'Failed to create payment order');
      }),
      catchError(error => {
        console.error('❌ Order creation error:', error);
        const errorMessage = error?.error?.error || error?.message || 'Failed to create payment order';
        this.toastService.error(errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Initiate payment with Razorpay
   */
  initiatePayment(
    orderId: string,
    amount: number,
    customerName: string,
    customerEmail: string,
    customerPhone: string,
    deliveryAddress: string
  ): Observable<PaymentResponse> {
    return new Observable(observer => {
      // Check if Razorpay is loaded
      if (!(window as any)['Razorpay']) {
        observer.error(new Error('Payment gateway is not ready. Please refresh the page and try again.'));
        return;
      }

      // Get appropriate key for environment
      const razorpayKey = this.getRazorpayKey();

      // Validate API key
      if (!razorpayKey || razorpayKey === 'rzp_test_YOUR_TEST_KEY') {
        observer.error(new Error('Invalid Razorpay API key. Please check your configuration.'));
        return;
      }

      // Create payment options with proper cancellation handling
      const options = {
        key: razorpayKey,
        amount: amount * 100, // Convert to paise
        currency: 'INR',
        name: 'Dr. Piyu Health Clinic',
        description: 'Natural Height Growth Solutions',
        order_id: orderId,
        prefill: {
          name: customerName,
          email: customerEmail,
          contact: customerPhone
        },
        notes: {
          address: deliveryAddress
        },
        theme: {
          color: '#4CAF50'
        },
        handler: (response: RazorpayResponse) => {
          observer.next({
            success: true,
            payment_id: response.razorpay_payment_id,
            order_id: response.razorpay_order_id,
            signature: response.razorpay_signature,
            status: 'success'
          });
          observer.complete();
        },
        modal: {
          ondismiss: () => {
            observer.next({ 
              success: false, 
              error: 'Payment cancelled by user', 
              status: 'cancelled' 
            });
            observer.complete();
          }
        },
        retry: {
          enabled: true,
          max_count: 3
        }
      };

      try {
        const rzp = new Razorpay(options);
        rzp.open();
      } catch (error) {
        observer.error({ success: false, error: 'Failed to initialize payment: ' + error, status: 'init_error' });
      }
    });
  }


  /**
   * Verify payment signature via Firebase Function
   */
  verifyPaymentSignature(
    orderId: string,
    paymentId: string,
    signature: string
  ): Observable<PaymentVerificationResponse> {
    if (!orderId || !paymentId || !signature) {
      return throwError(() => new Error('Missing required parameters for payment verification'));
    }

    const verificationRequest: PaymentVerificationRequest = {
      order_id: orderId,
      payment_id: paymentId,
      signature: signature
    };

    // Use Firebase Function for verification
    const endpoint = `${this.FIREBASE_FUNCTIONS_BASE}/verifyPayment`;

    return this.http.post<PaymentVerificationResponse>(endpoint, verificationRequest).pipe(
      map(response => {
        if (response?.success && response.verified) {
          return response;
        } else {
          return response;
        }
      }),
      catchError(error => {
        const errorMessage = error?.error?.error || error?.message || 'Payment verification failed';
        return of({
          success: false,
          verified: false,
          error: errorMessage
        });
      })
    );
  }

  /**
   * Get payment details via Firebase Function
   */
  getPaymentDetails(paymentId: string): Observable<PaymentDetailsResponse> {
    if (!paymentId) {
      return throwError(() => new Error('Payment ID is required'));
    }

    const endpoint = `${this.FIREBASE_FUNCTIONS_BASE}/getPaymentDetails?paymentId=${paymentId}`;

    return this.http.get<PaymentDetailsResponse>(endpoint).pipe(
      map(response => {
        if (response?.success && response.payment) {
          return response;
        }
        throw new Error(response?.error || 'Failed to get payment details');
      }),
      catchError(error => {
        const errorMessage = error?.error?.error || error?.message || 'Failed to get payment details';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Get order details via Firebase Function
   */
  getOrderDetails(orderId: string): Observable<OrderDetailsResponse> {
    if (!orderId) {
      return throwError(() => new Error('Order ID is required'));
    }

    const endpoint = `${this.FIREBASE_FUNCTIONS_BASE}/getOrderDetails?orderId=${orderId}`;

    return this.http.get<OrderDetailsResponse>(endpoint).pipe(
      map(response => {
        if (response?.success && response.order) {
          return response;
        }
        throw new Error(response?.error || 'Failed to get order details');
      }),
      catchError(error => {
        const errorMessage = error?.error?.error || error?.message || 'Failed to get order details';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Check payment status and verify it's successful
   */
  checkPaymentStatus(paymentId: string): Observable<{ success: boolean; verified: boolean; payment?: any; error?: string }> {
    return this.getPaymentDetails(paymentId).pipe(
      map(response => {
        if (response.success && response.payment) {
          const payment = response.payment;
          const isSuccessful = payment.status === 'captured' || payment.status === 'authorized';
          
          return {
            success: true,
            verified: isSuccessful,
            payment: payment
          };
        }
        return {
          success: false,
          verified: false,
          error: response.error || 'Failed to check payment status'
        };
      }),
      catchError(error => {
        return of({
          success: false,
          verified: false,
          error: error.message || 'Failed to check payment status'
        });
      })
    );
  }

  /**
   * Utility method to format amount for display
   */
  formatAmount(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  }

  /**
   * Get payment method display name
   */
  getPaymentMethodName(method: string): string {
    const methods: { [key: string]: string } = {
      'razorpay': 'Secure Online Payment',
      'upi': 'UPI Payment',
      'card': 'Credit/Debit Card',
      'netbanking': 'Net Banking',
      'wallet': 'Digital Wallet',
      'emi': 'EMI Payment'
    };
    return methods[method] || method;
  }

  /**
   * Validate payment amount
   */
  validateAmount(amount: number): boolean {
    return amount > 0 && amount <= 1000000; // Max 10 lakhs
  }

  /**
   * Validate phone number
   */
  validatePhoneNumber(phone: string): boolean {
    return /^[6-9]\d{9}$/.test(phone);
  }

  /**
   * Validate email
   */
  validateEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Generate receipt ID
   */
  generateReceiptId(): string {
    return 'receipt_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Handle payment error
   */
  handlePaymentError(error: any): string {
    if (error.error?.description) {
      return error.error.description;
    }
    if (error.error?.code) {
      return this.getErrorMessageByCode(error.error.code);
    }
    if (typeof error === 'string') {
      return error;
    }
    return 'Payment failed. Please try again.';
  }

  /**
   * Get error message by error code
   */
  private getErrorMessageByCode(code: string): string {
    const errorMessages: { [key: string]: string } = {
      'BAD_REQUEST_ERROR': 'Invalid payment request',
      'AUTHENTICATION_ERROR': 'Authentication failed',
      'SERVER_ERROR': 'Server error occurred',
      'GATEWAY_ERROR': 'Payment gateway error',
      'PAYMENT_DECLINED': 'Payment was declined',
      'INSUFFICIENT_FUNDS': 'Insufficient funds',
      'CARD_EXPIRED': 'Card has expired',
      'INVALID_CARD': 'Invalid card details'
    };
    return errorMessages[code] || 'Payment failed';
  }

  /**
   * Check if payment is in test mode
   */
  isTestMode(): boolean {
    return this.RAZORPAY_KEY.startsWith('rzp_test_');
  }

  /**
   * Get test card details for development
   */
  getTestCardDetails(): any {
    return {
      successful: {
        number: '4111 1111 1111 1111',
        expiry: '12/25',
        cvv: '123',
        name: 'Test User'
      },
      failed: {
        number: '4000 0000 0000 0002',
        expiry: '12/25',
        cvv: '123',
        name: 'Test User'
      }
    };
  }

  /**
   * Get test UPI IDs
   */
  getTestUpiIds(): any {
    return {
      successful: 'success@razorpay',
      failed: 'failure@razorpay'
    };
  }

  /**
   * Get supported payment methods
   */
  getSupportedPaymentMethods(): string[] {
    return ['upi', 'card', 'netbanking', 'wallet', 'emi'];
  }

  /**
   * Ensure Razorpay is ready for payment
   */
  ensureRazorpayReady(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.isRazorpayReady()) {
        resolve(true);
        return;
      }

      this.waitForRazorpayReady().then(() => {
        resolve(true);
      }).catch(() => {
        resolve(false);
      });
    });
  }
}
